import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiMail, FiHeart } from 'react-icons/fi';
import { FaInstagram, FaFacebookF, FaTiktok } from 'react-icons/fa';
import Icon from '../Icon/Icon';

const FooterContainer = styled(motion.footer)`
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${({ theme }) => theme.spacing.xl} 0;
  margin-top: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(38, 108, 127, 0.1) 0%,
      rgba(255, 143, 49, 0.05) 100%
    );
    pointer-events: none;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  position: relative;
  z-index: 2;
`;

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    justify-content: center;
    gap: calc(${({ theme }) => theme.spacing.xl} * 2);
  }
`;

const EmailCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.glass};
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 280px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 143, 49, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: auto;
    width: 100%;
    max-width: 320px;
  }
`;

const EmailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: white;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(255, 143, 49, 0.3);
`;

const EmailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EmailLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EmailAddress = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const SocialSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 20px;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 143, 49, 0.4);
  }
`;

const CopyrightSection = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BrandName = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
`;

const HeartIcon = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  animation: heartbeat 2s ease-in-out infinite;
  display: inline-flex;
  align-items: center;

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const CopyrightRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DeveloperLink = styled.a`
  color: ${({ theme }) => theme.colors.textLight};
  text-decoration: none;
  font-weight: 400;
  font-size: 0.7rem;
  opacity: 0.5;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    opacity: 0.7;
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textLight};
  text-decoration: none;
  font-size: 14px;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Footer: React.FC = () => {
  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
        ease: "easeOut" as const
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <FooterContainer
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <FooterContent>
        <EmailSection>
          <EmailCard
            variants={cardVariants}
            onClick={() => handleEmailClick('support@radyofone.com')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <EmailIcon>
              <Icon icon={FiMail} size={20} />
            </EmailIcon>
            <EmailInfo>
              <EmailLabel>Support</EmailLabel>
              <EmailAddress>support@radyofone.com</EmailAddress>
            </EmailInfo>
          </EmailCard>

          <EmailCard
            variants={cardVariants}
            onClick={() => handleEmailClick('marketing@radyofone.com')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <EmailIcon>
              <Icon icon={FiMail} size={20} />
            </EmailIcon>
            <EmailInfo>
              <EmailLabel>Marketing</EmailLabel>
              <EmailAddress>marketing@radyofone.com</EmailAddress>
            </EmailInfo>
          </EmailCard>
        </EmailSection>

        <Divider />

        <SocialSection>
          <SocialLink
            href="https://www.instagram.com/radyo_fone"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Radyofone on Instagram"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon icon={FaInstagram} size={20} />
          </SocialLink>
          <SocialLink
            href="https://www.facebook.com/share/1AdYHXENLj/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Radyofone on Facebook"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon icon={FaFacebookF} size={20} />
          </SocialLink>
          <SocialLink
            href="https://www.tiktok.com/@radyofone"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Radyofone on TikTok"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon icon={FaTiktok} size={20} />
          </SocialLink>
        </SocialSection>

        <CopyrightSection>
          <FooterLinks>
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
            <span>•</span>
            <FooterLink to="/account-deletion">Delete Account</FooterLink>
          </FooterLinks>
          <CopyrightRow>
            <span>© 2026 <BrandName>Radyofone</BrandName>. All rights reserved</span>
            <span>•</span>
            <span>Made with <HeartIcon><Icon icon={FiHeart} size={16} /></HeartIcon> for music lovers</span>
          </CopyrightRow>
          <DeveloperLink
            href="https://www.quantumbytech.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Developed by quantumbytech
          </DeveloperLink>
        </CopyrightSection>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
